import { RowNode } from '@ag-grid-community/core';
import { AgGridReact } from '@ag-grid-community/react';
import { Grid } from '@mui/material';
import { useAgGrid } from '@tassat/ag-grid';
import { AgGridFilterType } from '@tassat/common-definitions';
import React from 'react';

import { GridModelContext, ISideListProps } from './';

import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-material.css';
import './sideList.css';

export function SideList(props: ISideListProps) {
    const {
        classes,
        rowData,
        columnDefs,
        gridOptions
    } = props;

    const gridModel = React.useContext(GridModelContext);
    const removeHeaderAndApplyDefaultFilterOnReady = (params) => {
        if (!gridOptions?.headerHeight) {
            params.api.setGridOption('headerHeight', 0);
        }
        applyDefaultFilter(params);
        params.api.onFilterChanged();

        if (gridOptions?.sortModel) {
            params.api.applyColumnState({
                state: [gridOptions.sortModel],
                applyOrder: true
            });
        }
    };

    const applyDefaultFilter = (gridApi) => {
        if (gridOptions.defaultFilter && (!gridModel?.filterModel ||
            (gridModel.filterModel && gridModel.filterModel.colId !== gridOptions.defaultFilter.colId))) {
            const model = {
                filterType: 'text',
                type: gridOptions.defaultFilter.type,
                filter: gridOptions.defaultFilter.filter
            };
            gridApi.api.setColumnFilterModel(gridOptions.defaultFilter.colId, model);
        }
    };

    const [ gridApi, onGridReady ] = useAgGrid(removeHeaderAndApplyDefaultFilterOnReady);

    const getVisibleRecords = (afterFilterAndSort = false): RowNode[] => {
        const visibleNodes = [];
        gridApi?.api[afterFilterAndSort ? 'forEachNodeAfterFilterAndSort' : 'forEachNode'](node => {
            visibleNodes.push(node);
        });

        return visibleNodes;
    };

    React.useEffect((): void => {
        // Only refresh row data, not pinned row
        gridApi?.api.refreshCells({ rowNodes: getVisibleRecords() });
    }, [ gridApi, rowData ]);

    React.useEffect(() => {
        if (!gridApi) return;
        if (!gridModel) return;

        if (gridModel.sortModel) {
            const columnState = gridApi.api.getColumnState();
            const newSortModel = columnState.map(({ colId }) => ({
                colId,
                sort: colId === gridModel.sortModel?.colId ? gridModel.sortModel?.sort : null
            }));
            gridApi.api.applyColumnState({
                state: newSortModel,
                applyOrder: true
            });
        } else {
            gridApi.api.resetColumnState();
        }
    }, [gridModel?.sortModel]);

    React.useEffect(() => {
        if (!gridApi) return;
        if (!gridModel) return;

        gridApi.api.setFilterModel(null);
        applyDefaultFilter(gridApi);

        if (gridModel.searchValue && gridOptions.searchByColumn) {
            const model = {
                filterType: 'text',
                type: AgGridFilterType.Contains,
                filter: gridModel.searchValue
            };
            gridApi.api.setColumnFilterModel(gridOptions.searchByColumn, model);
        }

        if (gridModel.filterModel) {
            let model = {};

            if (typeof gridModel.filterModel.filter === 'number') {
                model = {
                    filterType: 'number',
                    type: gridModel.filterModel.type,
                    filter: gridModel.filterModel.filter
                };
            } else {
                model = {
                    filterType: 'text',
                    type: AgGridFilterType.Contains,
                    filter: gridModel.filterModel.filter.toString()
                };
            }
            gridApi.api.setColumnFilterModel(gridModel.filterModel.colId, model);
        }

        gridApi.api.onFilterChanged();
    }, [ gridModel?.searchValue, gridModel?.filterModel, gridOptions.defaultFilter ]);

    const handleFilterChange = (): void => {
        if (gridModel?.onFilterChange) {
            gridModel.onFilterChange(getVisibleRecords(true)
                .map((rowNode: RowNode): any => rowNode.data));
        }
    };

    return (
        <Grid
            className={classes.root}
            container
            spacing={0}
        >
            <Grid
                item
                xs={12}
            >
                <div
                    className='ag-theme-material'
                    id='sideListGrid'
                    style={{ height: '100%' }}
                >
                    <AgGridReact
                        animateRows={false}
                        columnDefs={columnDefs}
                        columnMenu='legacy'
                        defaultColDef={{ sortable: true }}
                        enableCellTextSelection={true}
                        getRowId={({ data, rowPinned }) => {
                            let id = data;
                            if (rowPinned && data.rowPinnedId) {
                                return data.rowPinnedId;
                            } else if (gridOptions.idField.includes('.')) {
                                gridOptions.idField.split('.')
                                    .forEach(field => {
                                        id = id[field];
                                    });
                            } else {
                                id = data[gridOptions.idField];
                            }

                            return id;
                        }}
                        headerHeight={gridOptions?.headerHeight || undefined}
                        onFilterChanged={handleFilterChange}
                        onGridReady={onGridReady}
                        pinnedTopRowData={gridOptions.pinnedRowData ?? undefined}
                        rowData={rowData}
                        rowHeight={gridOptions.cellHeight}
                        suppressCellFocus={true}
                        suppressContextMenu={true}
                    />
                </div>
            </Grid>
        </Grid>
    );
}